import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { ArrowLeft, Loader2, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface Vendor {
  id: string;
  name: string;
}

interface VendorsResponse {
  vendors: Vendor[];
}

const PRIORITIES = [
  { value: "URGENT", label: "Urgent" },
  { value: "HIGH", label: "High" },
  { value: "NORMAL", label: "Normal" },
  { value: "LOW", label: "Low" },
];

export default function NewVendorQuotePage() {
  const { sessionId } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const [vendorId, setVendorId] = useState("");
  const [vendorQuoteNumber, setVendorQuoteNumber] = useState("");
  const [quoteDate, setQuoteDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [validUntil, setValidUntil] = useState("");
  const [leadTimeDays, setLeadTimeDays] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [currencyCode, setCurrencyCode] = useState("USD");
  const [priority, setPriority] = useState("NORMAL");
  const [intendedPurpose, setIntendedPurpose] = useState("");
  const [notes, setNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");

  const { data: vendorsData } = useQuery<VendorsResponse>({
    queryKey: ["/api/vendors"],
    queryFn: () => api.get<VendorsResponse>(`/vendors`, sessionId),
    enabled: !!sessionId,
  });
  const vendors = vendorsData?.vendors || [];

  const createMutation = useMutation({
    mutationFn: async () => {
      return api.post<{ id: string }>(`/vendor-quotes`, {
        vendorId,
        vendorQuoteNumber: vendorQuoteNumber || null,
        quoteDate: quoteDate ? `${quoteDate}T00:00:00Z` : null,
        validUntil: validUntil ? `${validUntil}T00:00:00Z` : null,
        leadTimeDays: leadTimeDays ? parseInt(leadTimeDays) : null,
        paymentTerms: paymentTerms || null,
        currencyCode,
        priority,
        intendedPurpose: intendedPurpose || null,
        notes: notes || null,
        internalNotes: internalNotes || null,
        status: "DRAFT",
      }, sessionId);
    },
    onSuccess: (vq) => {
      toast({ title: "Vendor quote created" });
      setLocation(`/vendor-quotes/${vq.id}`);
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const canSubmit = !!vendorId && !createMutation.isPending;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => setLocation("/vendor-quotes")}
          className="text-muted-foreground hover:text-foreground transition-colors"
          data-testid="button-back"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
            <FileText className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">New Vendor Quote</h1>
            <p className="text-sm text-muted-foreground">
              Capture a quote received from a vendor for downstream conversion to a PO.
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-sm font-medium mb-1 block">
                Vendor <span className="text-destructive">*</span>
              </label>
              <Select value={vendorId} onValueChange={setVendorId}>
                <SelectTrigger data-testid="select-vendor">
                  <SelectValue placeholder="Select a vendor" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">No vendors found</div>
                  ) : (
                    vendors.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Vendor Quote #</label>
              <Input
                value={vendorQuoteNumber}
                onChange={(e) => setVendorQuoteNumber(e.target.value)}
                placeholder="Optional vendor's reference"
                data-testid="input-vendor-quote-number"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Priority</label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="select-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Quote Date</label>
              <Input
                type="date"
                value={quoteDate}
                onChange={(e) => setQuoteDate(e.target.value)}
                data-testid="input-quote-date"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Valid Until</label>
              <Input
                type="date"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
                data-testid="input-valid-until"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Lead Time (days)</label>
              <Input
                type="number"
                min={0}
                value={leadTimeDays}
                onChange={(e) => setLeadTimeDays(e.target.value)}
                placeholder="0"
                data-testid="input-lead-time"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Payment Terms</label>
              <Input
                value={paymentTerms}
                onChange={(e) => setPaymentTerms(e.target.value)}
                placeholder="NET30"
                data-testid="input-payment-terms"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Currency</label>
              <Input
                value={currencyCode}
                onChange={(e) => setCurrencyCode(e.target.value.toUpperCase())}
                maxLength={3}
                data-testid="input-currency"
              />
            </div>

            <div className="col-span-2">
              <label className="text-sm font-medium mb-1 block">Intended Purpose</label>
              <Textarea
                value={intendedPurpose}
                onChange={(e) => setIntendedPurpose(e.target.value)}
                placeholder="What is this quote for?"
                className="min-h-[60px]"
                data-testid="textarea-intended-purpose"
              />
            </div>

            <div className="col-span-2">
              <label className="text-sm font-medium mb-1 block">Notes</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="External notes shared with the vendor..."
                className="min-h-[80px]"
                data-testid="textarea-notes"
              />
            </div>

            <div className="col-span-2">
              <label className="text-sm font-medium mb-1 block">Internal Notes</label>
              <Textarea
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                placeholder="Internal-only context..."
                className="min-h-[80px]"
                data-testid="textarea-internal-notes"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setLocation("/vendor-quotes")}>
              Cancel
            </Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!canSubmit}
              data-testid="button-create-quote"
            >
              {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Vendor Quote
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
